import { ratingRangeStatus } from "./constants";

const RAW_RATING_RANGES = [
  { min: 95, label: "Legend" },
  { min: 85, label: "World Class" },
  { min: 65, label: "Strong" },
  { min: 45, label: "Average" },
  { min: 30, label: "Weak" },
  { min: 0, label: "Weakest" },
];

/** League vs cup/tournament stat helpers (missing → 0; null season cells stay null). */

export const asStat = (value) => {
  if (value == null || value === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Null stays null (archived season unknown); otherwise number. */
export const asSeasonStat = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const formatSeasonStat = (value) => {
  const n = asSeasonStat(value);
  return n == null ? "—" : String(n);
};

/** Convert either a public rating range (0–5) or a legacy raw rating to its label. */
export const formatPlayerRating = (value, ratingRange) => {
  const explicitRange = Number(ratingRange);
  if (Number.isInteger(explicitRange) && ratingRangeStatus[explicitRange]) {
    return ratingRangeStatus[explicitRange];
  }

  const n = asSeasonStat(value);
  if (n == null) return "—";
  if (Number.isInteger(n) && ratingRangeStatus[n]) {
    return ratingRangeStatus[n];
  }

  return RAW_RATING_RANGES.find(({ min }) => n >= min)?.label || "—";
};

export const seasonGoalsLeague = (p = {}) => asStat(p.goals_scored);
export const seasonGoalsCup = (p = {}) => asStat(p.goals_scored_cup);
export const seasonGoalsAll = (p = {}) =>
  seasonGoalsLeague(p) + seasonGoalsCup(p);

export const seasonAssistsLeague = (p = {}) => asStat(p.assists);
export const seasonAssistsCup = (p = {}) => asStat(p.assists_cup);
export const seasonAssistsAll = (p = {}) =>
  seasonAssistsLeague(p) + seasonAssistsCup(p);

export const careerGoalsAll = (p = {}) =>
  asStat(p.life_time_goals) + asStat(p.life_time_goals_cup);
export const careerAssistsAll = (p = {}) =>
  asStat(p.life_time_assists) + asStat(p.life_time_assists_cup);

export const seasonCleanSheetsAll = (p = {}) =>
  asStat(p.clean_sheets) + asStat(p.clean_sheets_cup);
export const careerCleanSheetsAll = (p = {}) =>
  asStat(p.clean_sheets_total) + asStat(p.clean_sheets_total_cup);

const DEF_POS = new Set(["GK", "LB", "RB", "DC", "DCL", "DCR", "DCC"]);

export const showsCleanSheets = (player = {}) => {
  const pos = String(player.fav_position || player.pos || "").toUpperCase();
  if (DEF_POS.has(pos)) return true;
  return pos.startsWith("DC") || pos === "GK" || pos === "LB" || pos === "RB";
};

/** Finite number or null (do not coerce missing stay stats to 0). */
export const finiteOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const entityId = (value) => {
  if (value == null) return null;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (value._id != null) return String(value._id);
  if (value.id != null) return String(value.id);
  return null;
};

const entityName = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return value;
  return value.name || value.club_name || value.team_name || null;
};

export const seasonClubId = (row = {}) =>
  entityId(row.club) ||
  entityId(row.team) ||
  entityId(row.club_id) ||
  entityId(row.team_id) ||
  null;

export const seasonClubName = (row = {}) =>
  entityName(row.club) ||
  entityName(row.team) ||
  row.club_name ||
  row.team_name ||
  row.clubName ||
  row.teamName ||
  null;

const sameClub = (row, clubId, clubName) => {
  const rowId = seasonClubId(row);
  if (clubId && rowId && clubId === rowId) return true;
  const rowName = seasonClubName(row);
  if (clubName && rowName && String(clubName).toLowerCase() === String(rowName).toLowerCase()) {
    return true;
  }
  return false;
};

/** Prefer an explicit total; else league+cup when either twin is present. */
const pickGoals = (obj = {}) => {
  const total = finiteOrNull(obj.goals ?? obj.goals_total);
  if (total != null) return total;
  if (
    obj.goals_scored != null ||
    obj.goals_scored_cup != null ||
    obj.goals_league != null ||
    obj.goals_cup != null
  ) {
    return (
      asStat(obj.goals_scored ?? obj.goals_league) +
      asStat(obj.goals_scored_cup ?? obj.goals_cup)
    );
  }
  return null;
};

const pickAssists = (obj = {}) => {
  const total = finiteOrNull(obj.assists_total);
  if (total != null) return total;
  if (
    obj.assists != null ||
    obj.assists_cup != null ||
    obj.assists_league != null
  ) {
    // Bare `assists` with no cup twin is the stay/season total.
    if (obj.assists_cup == null && obj.assists_league == null) {
      return finiteOrNull(obj.assists);
    }
    return asStat(obj.assists_league ?? obj.assists) + asStat(obj.assists_cup);
  }
  return null;
};

const sumField = (rows, pick) => {
  let sum = 0;
  let any = false;
  rows.forEach((row) => {
    const value = pick(row);
    if (value == null) return;
    sum += value;
    any = true;
  });
  return any ? sum : null;
};

/**
 * Stay totals for the player's current club from `GET /league/players/:id/career`.
 * Uses `current_club` fields when present; otherwise sums season rows that match
 * that club. Returns null when the payload has no current club.
 */
export const clubStayFromCareer = (career) => {
  const current = career?.current_club;
  if (!current || typeof current !== "object") return null;

  const team = current.team || current.club || {};
  const clubId = entityId(team) || entityId(current.team_id) || entityId(current.club_id);
  const clubName =
    entityName(team) || entityName(current) || current.club_name || current.team_name || null;

  let appearances = finiteOrNull(
    current.appearances ?? current.apps ?? current.played_matches
  );
  let goals = pickGoals(current);
  let assists = pickAssists(current);

  const seasons = Array.isArray(career.seasons) ? career.seasons : [];
  const atClub = seasons.filter((row) => sameClub(row, clubId, clubName));

  if (appearances == null && atClub.length) {
    appearances = sumField(atClub, (row) =>
      finiteOrNull(row.played_matches ?? row.appearances ?? row.apps)
    );
  }
  if (goals == null && atClub.length) {
    goals = sumField(atClub, pickGoals);
  }
  if (assists == null && atClub.length) {
    assists = sumField(atClub, pickAssists);
  }

  if (appearances == null && goals == null && assists == null && !clubName) {
    return null;
  }

  return {
    clubId,
    clubName,
    appearances,
    goals,
    assists,
    playtimeThisSeason: finiteOrNull(current.playtime_this_season),
  };
};
